import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import {
  Camera,
  FileImage,
  FileText,
  Upload,
  Loader2,
  ArrowRight,
  User,
  Calendar,
  Hash,
  Stethoscope,
  CheckCircle2,
  Sparkles,
  X,
  Pencil,
  Search,
  FileCheck,
  AlertTriangle,
  Images,
  Plus,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import type { ScheduledCase, SurgeonTemplate, Laterality } from '@/lib/templates/types';
import { supabase } from '@/integrations/supabase/client';
import type { ClinicalSeverityData } from '@/lib/clinicalSeverityTypes';
import { inferAnatomyFromCPT } from '@/lib/denialDefender/utils';
import { useDemoMode } from '@/contexts/DemoModeContext';

type DropzoneState = 'idle' | 'dragging' | 'staging' | 'processing' | 'complete' | 'error';

interface StagedFile {
  id: string;
  file: File;
  name: string;
  type: string;
  thumbnailUrl: string | null;
}

interface MatchedTemplateResult {
  template: SurgeonTemplate;
  confidence: number;
  matchedKeywords: string[];
}

interface ExtractedPatientData {
  patient_name: string | null;
  patient_dob: string | null;
  patient_mrn: string | null;
  patient_sex: string | null;
  encounter_id: string | null;
  scheduled_procedure: string | null;
  facility_name: string | null;
  payer_name: string | null;
  surgeon_name: string | null;
  scheduled_date: string | null;
  clinical_context: string | null;
  laterality: Laterality | null;
  extraction_confidence: 'high' | 'medium' | 'low';
  extraction_notes: string | null;
}

// Extract laterality from raw procedure text
function extractLateralityFromText(text: string): Laterality | null {
  const lower = text.toLowerCase();
  if (/\bbilateral\b/.test(lower)) return 'bilateral';
  if (/\bright\b|\b-rt\b|\brt\s/.test(lower)) return 'right';
  if (/\bleft\b|\b-lt\b|\blt\s/.test(lower)) return 'left';
  return null;
}

function firstMatch(text: string, patterns: RegExp[]): string | null {
  for (const pattern of patterns) {
    const match = text.match(pattern);
    const value = match?.[1]?.trim();
    if (value) return value.replace(/\s+/g, ' ');
  }
  return null;
}

function buildDemoExtractionFallback(files: StagedFile[], rawText: string): ExtractedPatientData {
  const fileCorpus = files.map(file => file.name).join(' ');
  const corpus = [rawText, fileCorpus].join('\n').trim();
  const compactCorpus = corpus.replace(/\s+/g, ' ');

  const patientName = firstMatch(compactCorpus, [
    /(?:patient|name)\s*[:#-]\s*([A-Z][A-Za-z'.-]+(?:\s+[A-Z][A-Za-z'.-]+){0,3})/i,
    /(?:for|re)\s+([A-Z][A-Za-z'.-]+(?:\s+[A-Z][A-Za-z'.-]+){1,3})\s+(?:dob|mrn|procedure|surgery)/i,
  ]);

  const patientDob = firstMatch(compactCorpus, [
    /(?:dob|date of birth)\s*[:#-]\s*(\d{1,2}[/-]\d{1,2}[/-]\d{2,4})/i,
    /\b(\d{1,2}[/-]\d{1,2}[/-]\d{2,4})\b/,
  ]);

  const patientMrn = firstMatch(compactCorpus, [
    /(?:mrn|medical record)\s*[:#-]\s*([A-Z0-9-]{3,})/i,
  ]);

  const procedure = firstMatch(compactCorpus, [
    /(?:procedure|surgery|planned procedure|scheduled procedure)\s*[:#-]\s*([^.\n]{8,120})/i,
    /\b((?:left|right|bilateral)\s+[^.\n]*(?:arthroplasty|fusion|arthroscopy|repair|debridement|injection|laminectomy|meniscectomy|rotator cuff)[^.\n]*)/i,
    /\b([^.\n]*(?:arthroplasty|fusion|arthroscopy|repair|debridement|laminectomy|meniscectomy|rotator cuff)[^.\n]*)/i,
  ]);

  const facility = firstMatch(compactCorpus, [
    /(?:facility|location|hospital|asc)\s*[:#-]\s*([^.\n]{3,80})/i,
  ]);

  const payer = firstMatch(compactCorpus, [
    /(?:payer|insurance|plan)\s*[:#-]\s*([^.\n]{3,80})/i,
  ]);

  const encounterId = firstMatch(compactCorpus, [
    /(?:encounter|csn|case)\s*[:#-]\s*([A-Z0-9-]{3,})/i,
  ]);

  return {
    patient_name: patientName,
    patient_dob: patientDob,
    patient_mrn: patientMrn,
    patient_sex: null,
    encounter_id: encounterId,
    scheduled_procedure: procedure || 'Uploaded clinical document - procedure pending review',
    facility_name: facility,
    payer_name: payer || 'Demo Payer',
    surgeon_name: null,
    scheduled_date: new Date().toISOString(),
    clinical_context: rawText.trim()
      ? rawText.trim().slice(0, 4000)
      : `Demo fallback created from uploaded file${files.length > 1 ? 's' : ''}: ${fileCorpus || 'clinical document'}.`,
    laterality: extractLateralityFromText(compactCorpus),
    extraction_confidence: 'low',
    extraction_notes: 'The extraction service was unavailable, so LINK created a draft from local document text and file names. Review the fields before continuing.',
  };
}

// CPT prefix → anatomy mapping for template routing
const CPT_PREFIX_ANATOMY_MAP: Record<string, string[]> = {
  '298': ['shoulder', 'knee'],   // Arthroscopy codes (29827, 29881, etc.)
  '234': ['shoulder'],           // Shoulder repair (23412, 23420, etc.)
  '271': ['knee', 'hip'],       // Knee/hip repair
  '272': ['knee'],              // Knee procedures
  '273': ['hip'],               // Hip procedures
  '280': ['foot', 'ankle'],     // Foot/ankle
  '282': ['foot'],              // Foot procedures
  '150': ['hand'],              // Flap/graft
  '276': ['knee', 'hip'],       // Tendon repair
  '287': ['foot', 'ankle'],     // Foot/ankle correction
};

// Match templates by CPT code prefix + anatomy fallback
function cptPrefixMatchTemplates(
  cptCode: string,
  templates: SurgeonTemplate[],
): MatchedTemplateResult[] {
  const results: MatchedTemplateResult[] = [];
  const code = cptCode.trim();

  for (const template of templates) {
    const allCpts = [
      ...template.primary_cpts.map(c => c.code),
      ...template.secondary_cpts.map(c => c.code),
    ];

    // Exact CPT match
    if (allCpts.includes(code)) {
      results.push({ template, confidence: 1.0, matchedKeywords: [code] });
      continue;
    }

    // Prefix match: if CPT shares a 3-char prefix with any template CPT
    const prefix = code.substring(0, 3);
    const hasPrefix = allCpts.some(c => c.startsWith(prefix));
    if (hasPrefix) {
      results.push({ template, confidence: 0.75, matchedKeywords: [`prefix:${prefix}`] });
    }
  }

  return results.sort((a, b) => b.confidence - a.confidence);
}

// Anatomy-based fallback matching
function anatomyFallbackMatch(
  targetAnatomy: string,
  templates: SurgeonTemplate[],
): MatchedTemplateResult | null {
  const anatomyKeywords: Record<string, string[]> = {
    shoulder: ['shoulder', 'rotator', 'cuff', 'labrum', 'glenohumeral', 'acromi'],
    knee: ['knee', 'meniscus', 'acl', 'pcl', 'patella', 'tibial'],
    hip: ['hip', 'acetabul', 'femoral', 'trochant'],
    spine: ['spine', 'lumbar', 'cervical', 'thoracic', 'disc', 'vertebr'],
    foot: ['foot', 'metatarsal', 'hallux', 'bunion', 'plantar', 'toe'],
    ankle: ['ankle', 'talus', 'fibula', 'peroneal'],
    hand: ['hand', 'carpal', 'finger', 'wrist', 'metacarpal'],
  };

  const keywords = anatomyKeywords[targetAnatomy.toLowerCase()] || [];
  if (keywords.length === 0) return null;

  for (const template of templates) {
    const corpus = [template.template_name, template.body_region || '', template.specialty]
      .join(' ')
      .toLowerCase();
    if (keywords.some(kw => corpus.includes(kw))) {
      return { template, confidence: 0.5, matchedKeywords: [`anatomy:${targetAnatomy}`] };
    }
  }
  return null;
}

// Semantic matching: compare raw procedure text against template names + CPT descriptions
function semanticMatchTemplates(
  rawProcedureText: string,
  templates: SurgeonTemplate[]
): MatchedTemplateResult[] {
  const normalizedInput = rawProcedureText.toLowerCase().replace(/[^a-z0-9\s]/g, '');
  const inputTokens = normalizedInput.split(/\s+/).filter(t => t.length > 2);

  // Extract CPT codes from input text
  const cptMatches = rawProcedureText.match(/\b\d{5}\b/g) || [];

  // Try CPT prefix matching first
  for (const cpt of cptMatches) {
    const cptResults = cptPrefixMatchTemplates(cpt, templates);
    if (cptResults.length > 0) return cptResults;
  }

  // Try anatomy inference from CPT codes
  for (const cpt of cptMatches) {
    const anatomy = inferAnatomyFromCPT(cpt);
    if (anatomy !== 'unspecified') {
      const fallback = anatomyFallbackMatch(anatomy, templates);
      if (fallback) return [fallback];
    }
  }

  // Original semantic token matching as last resort
  const results: MatchedTemplateResult[] = [];

  for (const template of templates) {
    const corpus = [
      template.template_name,
      template.body_region || '',
      template.specialty || '',
      ...template.primary_cpts.map(c => c.description),
      ...template.secondary_cpts.map(c => c.description),
    ]
      .join(' ')
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '');

    const corpusTokens = new Set(corpus.split(/\s+/).filter(t => t.length > 2));
    const matchedKeywords: string[] = [];

    for (const token of inputTokens) {
      for (const corpusToken of corpusTokens) {
        if (corpusToken.includes(token) || token.includes(corpusToken)) {
          matchedKeywords.push(token);
          break;
        }
      }
    }

    const confidence = inputTokens.length > 0 ? matchedKeywords.length / inputTokens.length : 0;
    if (confidence > 0.15) {
      results.push({ template, confidence, matchedKeywords: [...new Set(matchedKeywords)] });
    }
  }

  return results.sort((a, b) => b.confidence - a.confidence);
}

import {
  fileToBase64,
  inferMimeType,
  getExtractionStrategy,
  extractTextFromFile,
  getUnsupportedFileMessage,
} from '@/lib/fileExtraction';

interface SoloSurgeonDropzoneProps {
  onPatientExtracted: (patient: ScheduledCase, autoMatchedTemplate?: SurgeonTemplate, extractedLaterality?: Laterality | null, clinicalContext?: string, clinicalSeverityData?: ClinicalSeverityData | null) => void;
}

type IntakeMode = 'upload' | 'manual';

interface ManualPatientForm {
  patientName: string;
  dob: string;
  mrn: string;
  sex: string;
  procedure: string;
  facility: string;
  payer: string;
  encounterId: string;
}

const emptyManualForm: ManualPatientForm = {
  patientName: '',
  dob: '',
  mrn: '',
  sex: '',
  procedure: '',
  facility: '',
  payer: '',
  encounterId: '',
};

/** Create a thumbnail URL for image files, null for non-images */
function createThumbnail(file: File): string | null {
  if (file.type.startsWith('image/')) {
    return URL.createObjectURL(file);
  }
  return null;
}

export function SoloSurgeonDropzone({ onPatientExtracted }: SoloSurgeonDropzoneProps) {
  const { isDemoMode } = useDemoMode();
  const [intakeMode, setIntakeMode] = useState<IntakeMode>('upload');
  const [dropzoneState, setDropzoneState] = useState<DropzoneState>('idle');
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const [extractedData, setExtractedData] = useState<ExtractedPatientData | null>(null);
  const [detectedProcedureText, setDetectedProcedureText] = useState('');
  const [clinicalContext, setClinicalContext] = useState('');
  const [clinicalSeverityData, setClinicalSeverityData] = useState<ClinicalSeverityData | null>(null);
  const [matchedTemplate, setMatchedTemplate] = useState<SurgeonTemplate | null>(null);
  const [matchConfidence, setMatchConfidence] = useState(0);
  const [allTemplates, setAllTemplates] = useState<SurgeonTemplate[]>([]);
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');
  const [templatePopoverOpen, setTemplatePopoverOpen] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [manualForm, setManualForm] = useState<ManualPatientForm>(emptyManualForm);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const addMoreInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Editable demographic overrides for missing extractions
  const [editableName, setEditableName] = useState('');
  const [editableDob, setEditableDob] = useState('');
  const [editableMrn, setEditableMrn] = useState('');
  // Load templates on mount
  useEffect(() => {
    const loadTemplates = async () => {
      const { data } = await supabase
        .from('surgeon_templates')
        .select('*')
        .eq('is_active', true)
        .order('template_name');
      if (data) {
        const templates: SurgeonTemplate[] = data.map((d: any) => ({
          ...d,
          primary_cpts: Array.isArray(d.primary_cpts) ? d.primary_cpts : JSON.parse(d.primary_cpts || '[]'),
          secondary_cpts: Array.isArray(d.secondary_cpts) ? d.secondary_cpts : JSON.parse(d.secondary_cpts || '[]'),
          common_addons: Array.isArray(d.common_addons) ? d.common_addons : JSON.parse(d.common_addons || '[]'),
          diagnosis_codes: Array.isArray(d.diagnosis_codes) ? d.diagnosis_codes : JSON.parse(d.diagnosis_codes || '[]'),
        }));
        setAllTemplates(templates);
      }
    };
    loadTemplates();
  }, []);

  // Global paste listener
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      if (dropzoneState === 'processing' || dropzoneState === 'complete') return;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            stageFiles([file]);
          }
          return;
        }
      }
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [dropzoneState]);

  /** Stage files for review (does NOT process yet) */
  const stageFiles = useCallback((files: File[]) => {
    const newStaged: StagedFile[] = files.map(file => {
      const resolvedMimeType = inferMimeType(file);
      const strategy = getExtractionStrategy(resolvedMimeType);

      if (strategy === 'unsupported') {
        const msg = getUnsupportedFileMessage(file, resolvedMimeType);
        toast.error('Unsupported file type', { description: msg });
        return null;
      }

      return {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        name: file.name || `Capture (${new Date().toLocaleTimeString()})`,
        type: resolvedMimeType,
        thumbnailUrl: createThumbnail(file),
      };
    }).filter(Boolean) as StagedFile[];

    if (newStaged.length === 0) return;

    setStagedFiles(prev => [...prev, ...newStaged]);
    setDropzoneState('staging');
    setErrorMessage('');
  }, []);

  /** Remove a staged file */
  const removeStagedFile = useCallback((fileId: string) => {
    setStagedFiles(prev => {
      const updated = prev.filter(f => {
        if (f.id === fileId && f.thumbnailUrl) {
          URL.revokeObjectURL(f.thumbnailUrl);
        }
        return f.id !== fileId;
      });
      if (updated.length === 0) {
        setDropzoneState('idle');
      }
      return updated;
    });
  }, []);

  /** Process all staged files as a single batch */
  const processBatch = useCallback(async () => {
    if (stagedFiles.length === 0) return;

    setDropzoneState('processing');
    setErrorMessage('');
    let rawTextForFallback = '';

    try {
      // Separate vision files (images/PDFs) from text files
      const visionFiles: StagedFile[] = [];
      const textFiles: StagedFile[] = [];

      for (const sf of stagedFiles) {
        const strategy = getExtractionStrategy(sf.type);
        if (strategy === 'vision') {
          visionFiles.push(sf);
        } else if (strategy === 'text') {
          textFiles.push(sf);
        }
      }

      // Build the request body
      let requestBody: Record<string, unknown>;

      if (visionFiles.length > 0) {
        // Convert all vision files to base64 and send as batch
        const imagesPayload = await Promise.all(
          visionFiles.map(async (sf) => ({
            base64: await fileToBase64(sf.file),
            mimeType: sf.type,
          }))
        );

        // If there are also text files, extract and append as text context
        let combinedText = '';
        for (const tf of textFiles) {
          const text = await extractTextFromFile(tf.file, tf.type);
          if (text.trim()) {
            combinedText += `\n\n--- Document: ${tf.name} ---\n${text}`;
          }
        }
        rawTextForFallback = combinedText.trim();

        if (imagesPayload.length === 1 && !combinedText) {
          // Single image: use legacy format for backward compatibility
          requestBody = { imageBase64: imagesPayload[0].base64, mimeType: imagesPayload[0].mimeType };
        } else {
          requestBody = { images: imagesPayload };
          if (combinedText) {
            requestBody.rawText = combinedText;
          }
        }
      } else {
        // Text-only: combine all text files
        let combinedText = '';
        for (const tf of textFiles) {
          const text = await extractTextFromFile(tf.file, tf.type);
          if (text.trim()) {
            combinedText += `\n\n--- Document: ${tf.name} ---\n${text}`;
          }
        }
        rawTextForFallback = combinedText.trim();
        if (!combinedText.trim()) {
          throw new Error('No readable text found in the documents. Try uploading images or PDFs instead.');
        }
        requestBody = { rawText: combinedText.trim() };
      }

      // Fire patient extraction
      const { data, error } = await supabase.functions.invoke('extract-patient-universal', {
        body: requestBody,
      });

      if (error) {
        const fallbackMessage = 'Could not extract data from these documents. Please try different formats or use Manual Entry.';
        const normalizedMessage = error.message?.includes('non-2xx')
          ? fallbackMessage
          : (error.message || fallbackMessage);
        throw new Error(normalizedMessage);
      }

      const result = data as ExtractedPatientData;
      setExtractedData(result);

      const procedureText = result.scheduled_procedure || '';
      setDetectedProcedureText(procedureText);
      setClinicalContext(result.clinical_context || '');

      // Fire clinical severity extraction in background (non-blocking)
      supabase.functions.invoke('extract-clinical-severity', { body: requestBody })
        .then(({ data: sevData, error: sevErr }) => {
          if (!sevErr && sevData) {
            setClinicalSeverityData(sevData as ClinicalSeverityData);
            console.log('[extract-clinical-severity] Structured clinical data extracted:', sevData);
          } else {
            console.warn('[extract-clinical-severity] Non-critical failure:', sevErr);
          }
        });

      if (procedureText) {
        const matches = semanticMatchTemplates(procedureText, allTemplates);
        if (matches.length > 0) {
          setMatchedTemplate(matches[0].template);
          setMatchConfidence(matches[0].confidence);
        }
      }

      setDropzoneState('complete');

      if (result.extraction_confidence === 'low') {
        toast.warning('Low Confidence Extraction', {
          description: result.extraction_notes || 'Some fields may be inaccurate. Please verify.',
        });
      } else {
        toast.success('Extraction Complete', {
          description: `${stagedFiles.length} page${stagedFiles.length > 1 ? 's' : ''} processed as a single document.`,
        });
      }
    } catch (err: unknown) {
      const message = err instanceof Error
        ? err.message
        : 'Failed to extract patient data from the documents.';

      console.error('Batch extraction failed:', err);

      if (isDemoMode) {
        const fallback = buildDemoExtractionFallback(stagedFiles, rawTextForFallback);
        setExtractedData(fallback);
        setEditableName(fallback.patient_name || 'Demo Upload Patient');
        setEditableDob(fallback.patient_dob || '');
        setEditableMrn(fallback.patient_mrn || '');
        setDetectedProcedureText(fallback.scheduled_procedure || '');
        setClinicalContext(fallback.clinical_context || '');
        setClinicalSeverityData(null);

        if (fallback.scheduled_procedure) {
          const matches = semanticMatchTemplates(fallback.scheduled_procedure, allTemplates);
          if (matches.length > 0) {
            setMatchedTemplate(matches[0].template);
            setMatchConfidence(matches[0].confidence);
          }
        }

        setDropzoneState('complete');
        toast.warning('Draft Created from Upload', {
          description: 'The extraction service did not respond, so demo mode created a reviewable draft instead.',
        });
        return;
      }

      setDropzoneState('error');
      setErrorMessage(message);
      toast.error('Extraction Failed', { description: message });
    }
  }, [stagedFiles, allTemplates, isDemoMode]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropzoneState === 'idle' || dropzoneState === 'staging') setDropzoneState('dragging');
  }, [dropzoneState]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropzoneState === 'dragging') {
      setDropzoneState(stagedFiles.length > 0 ? 'staging' : 'idle');
    }
  }, [dropzoneState, stagedFiles.length]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropzoneState === 'processing' || dropzoneState === 'complete') return;
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) stageFiles(files);
  }, [dropzoneState, stageFiles]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length > 0) stageFiles(files);
    if (e.target) e.target.value = '';
  }, [stageFiles]);

  // ── Camera functions ──
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const handleTakePhoto = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (isMobile) {
      cameraInputRef.current?.click();
    } else {
      startWebcam();
    }
  }, [isMobile]);

  const startWebcam = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
      });
      streamRef.current = stream;
      setCameraActive(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      }, 50);
    } catch (err) {
      console.error('Camera access denied:', err);
      toast.error('Camera access denied', {
        description: 'Please allow camera access in your browser settings, or use "Browse Files" instead.',
      });
    }
  }, []);

  const stopWebcam = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  }, []);

  const captureFrame = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    stopWebcam();

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `Camera Capture (${new Date().toLocaleTimeString()}).jpg`, {
          type: 'image/jpeg',
        });
        stageFiles([file]);
      }
    }, 'image/jpeg', 0.92);
  }, [stopWebcam, stageFiles]);

  // Cleanup webcam + thumbnail URLs on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
      stagedFiles.forEach(f => {
        if (f.thumbnailUrl) URL.revokeObjectURL(f.thumbnailUrl);
      });
    };
  }, []);

  const handleReset = () => {
    // Revoke all thumbnail URLs
    stagedFiles.forEach(f => {
      if (f.thumbnailUrl) URL.revokeObjectURL(f.thumbnailUrl);
    });
    setDropzoneState('idle');
    setStagedFiles([]);
    setExtractedData(null);
    setDetectedProcedureText('');
    setClinicalContext('');
    setClinicalSeverityData(null);
    setMatchedTemplate(null);
    setMatchConfidence(0);
    setErrorMessage('');
    setManualForm(emptyManualForm);
    stopWebcam();
  };

  const updateManualField = (field: keyof ManualPatientForm, value: string) => {
    setManualForm(prev => ({ ...prev, [field]: value }));
  };

  const handleManualProceed = () => {
    if (!manualForm.patientName.trim()) {
      toast.error('Patient name is required');
      return;
    }
    const procedureText = manualForm.procedure.trim();
    let autoTemplate: SurgeonTemplate | undefined;
    let extractedLaterality: Laterality | null = null;

    if (procedureText) {
      extractedLaterality = extractLateralityFromText(procedureText);
      const matches = semanticMatchTemplates(procedureText, allTemplates);
      if (matches.length > 0 && matches[0].confidence >= 0.4) {
        autoTemplate = matches[0].template;
      }
    }

    const enrichedCase: ScheduledCase = {
      id: `solo-manual-${Date.now()}`,
      patient_name: manualForm.patientName.trim(),
      patient_dob: manualForm.dob || null,
      patient_mrn: manualForm.mrn || null,
      patient_sex: manualForm.sex || null,
      encounter_id: manualForm.encounterId || null,
      scheduled_date: new Date().toISOString(),
      scheduled_procedure: procedureText || null,
      facility_name: manualForm.facility || null,
      payer_name: manualForm.payer || 'Solo Surgeon Mode',
      plan_name: null,
      surgeon_id: '',
      facility_id: null,
      member_id: null,
      status: 'scheduled',
      is_demo: false,
    };
    onPatientExtracted(enrichedCase, autoTemplate, extractedLaterality, '');
  };

  // Denial Defender intercept state

  const handleProceed = () => {
    if (!extractedData) return;

    // Use editable overrides for demographics
    const patientName = editableName || extractedData.patient_name || '';
    const patientDob = editableDob || extractedData.patient_dob || null;
    const patientMrn = editableMrn || extractedData.patient_mrn || null;

    if (!patientName.trim()) {
      toast.error('Patient name is required');
      return;
    }

    // Denial Defender threshold gaps are surfaced on the RCM Audit panel at the
    // Code Cart → Authorization step, where the surgeon can apply fixes, attach
    // documents, or flag individual items for the office manager. We no longer
    // block at upload time — that was duplicative and forced the surgeon to
    // answer questions before they had a chance to review the case.

    const extractedLaterality = extractedData.laterality || extractLateralityFromText(detectedProcedureText);
    const enrichedCase: ScheduledCase = {
      id: `solo-${Date.now()}`,
      patient_name: patientName,
      patient_dob: patientDob,
      patient_mrn: patientMrn,
      patient_sex: extractedData.patient_sex || null,
      encounter_id: extractedData.encounter_id || null,
      scheduled_date: extractedData.scheduled_date || new Date().toISOString(),
      scheduled_procedure: detectedProcedureText || null,
      facility_name: extractedData.facility_name || null,
      payer_name: extractedData.payer_name || 'Solo Surgeon Mode',
      plan_name: null,
      surgeon_id: '',
      facility_id: null,
      member_id: null,
      status: 'scheduled',
      is_demo: false,
    };
    onPatientExtracted(enrichedCase, matchedTemplate || undefined, extractedLaterality, clinicalContext, clinicalSeverityData);
  };

  const handleTemplateOverride = (template: SurgeonTemplate) => {
    setMatchedTemplate(template);
    setMatchConfidence(1);
    setTemplatePopoverOpen(false);
    setTemplateSearchQuery('');
    toast.info('Template Updated', { description: `Switched to: ${template.template_name}` });
  };

  const filteredTemplates = useMemo(() => {
    if (!templateSearchQuery.trim()) return allTemplates;
    const q = templateSearchQuery.toLowerCase();
    return allTemplates.filter(
      t =>
        t.template_name.toLowerCase().includes(q) ||
        t.specialty.toLowerCase().includes(q) ||
        (t.body_region && t.body_region.toLowerCase().includes(q))
    );
  }, [allTemplates, templateSearchQuery]);

  const confidenceLabel = matchConfidence >= 0.7 ? 'High' : matchConfidence >= 0.4 ? 'Medium' : 'Low';
  const confidenceColor =
    matchConfidence >= 0.7
      ? 'text-green-600 bg-green-500/10 border-green-500/30'
      : matchConfidence >= 0.4
        ? 'text-yellow-600 bg-yellow-500/10 border-yellow-500/30'
        : 'text-orange-600 bg-orange-500/10 border-orange-500/30';

  const extractionConfidenceColor =
    extractedData?.extraction_confidence === 'high'
      ? 'text-green-600 bg-green-500/10'
      : extractedData?.extraction_confidence === 'medium'
        ? 'text-yellow-600 bg-yellow-500/10'
        : 'text-orange-600 bg-orange-500/10';

  return (
    <div className="space-y-4">
      {/* Solo Surgeon Mode Banner */}
      <div className="flex items-center gap-3 p-3.5 rounded-xl bg-sky-50 border border-sky-200">
        <Sparkles className="w-5 h-5 text-sky-600 shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-sky-900">Solo Surgeon Mode</p>
          <p className="text-xs text-sky-700/70">
            No EHR integration — upload or photograph clinical documents to begin.
          </p>
        </div>
        <Badge variant="outline" className="text-sky-600 border-sky-200 text-[10px] bg-white/60">STANDALONE</Badge>
      </div>

      {/* Mode Toggle */}
      <div className="flex gap-1 p-1 rounded-lg bg-muted/50 border border-border">
        <button
          onClick={() => setIntakeMode('upload')}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all flex-1 justify-center',
            intakeMode === 'upload'
              ? 'bg-background text-foreground shadow-sm border border-border/60'
              : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
          )}
        >
          <Upload className="w-4 h-4" />
          Upload Document
        </button>
        <button
          onClick={() => setIntakeMode('manual')}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all flex-1 justify-center',
            intakeMode === 'manual'
              ? 'bg-background text-foreground shadow-sm border border-border/60'
              : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
          )}
        >
          <Pencil className="w-4 h-4" />
          Manual Entry
        </button>
      </div>

      {/* Manual Entry Form */}
      {intakeMode === 'manual' && (
        <Card className="border-border shadow-sm">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-2 mb-1">
              <User className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Patient Information</h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2 space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Patient Name <span className="text-destructive">*</span>
                </label>
                <Input
                  placeholder="Last, First"
                  value={manualForm.patientName}
                  onChange={e => updateManualField('patientName', e.target.value)}
                  className="h-9"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Date of Birth</label>
                <Input
                  type="date"
                  value={manualForm.dob}
                  onChange={e => updateManualField('dob', e.target.value)}
                  className="h-9"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">MRN</label>
                <Input
                  placeholder="Medical Record Number"
                  value={manualForm.mrn}
                  onChange={e => updateManualField('mrn', e.target.value)}
                  className="h-9"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Sex</label>
                <div className="flex gap-1.5">
                  {['male', 'female'].map(s => (
                    <Button
                      key={s}
                      type="button"
                      size="sm"
                      variant={manualForm.sex === s ? 'default' : 'outline'}
                      className="flex-1 h-9 capitalize"
                      onClick={() => updateManualField('sex', manualForm.sex === s ? '' : s)}
                    >
                      {s}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">CSN / Encounter</label>
                <Input
                  placeholder="Encounter ID"
                  value={manualForm.encounterId}
                  onChange={e => updateManualField('encounterId', e.target.value)}
                  className="h-9"
                />
              </div>
            </div>

            <div className="border-t border-border pt-4 space-y-3">
              <div className="flex items-center gap-2">
                <Stethoscope className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-semibold text-foreground">Clinical Details</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2 space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Procedure</label>
                  <Input
                    placeholder="e.g., Total Knee Arthroplasty"
                    value={manualForm.procedure}
                    onChange={e => updateManualField('procedure', e.target.value)}
                    className="h-9"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Facility</label>
                  <Input
                    placeholder="Facility name"
                    value={manualForm.facility}
                    onChange={e => updateManualField('facility', e.target.value)}
                    className="h-9"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Insurance Payer</label>
                  <Input
                    placeholder="e.g., UnitedHealthcare"
                    value={manualForm.payer}
                    onChange={e => updateManualField('payer', e.target.value)}
                    className="h-9"
                  />
                </div>
              </div>
            </div>

            <Button
              onClick={handleManualProceed}
              className="w-full"
              disabled={!manualForm.patientName.trim()}
            >
              Proceed with Patient
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Dropzone (only in upload mode) */}
      {intakeMode === 'upload' && (
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => (dropzoneState === 'idle') && !cameraActive && fileInputRef.current?.click()}
        className={cn(
          'relative rounded-xl border-2 border-dashed transition-all duration-300 cursor-pointer bg-white',
          (dropzoneState === 'idle') && !cameraActive && 'border-sky-300 hover:border-sky-500 hover:border-solid hover:bg-sky-50/50',
          cameraActive && 'border-sky-500 bg-black/95 cursor-default',
          dropzoneState === 'dragging' && 'border-sky-500 border-solid bg-sky-50 scale-[1.01]',
          dropzoneState === 'staging' && 'border-sky-400 border-solid bg-white cursor-default',
          dropzoneState === 'processing' && 'border-sky-400 bg-sky-50/50 cursor-default',
          dropzoneState === 'complete' && 'border-sky-300 bg-sky-50/30 cursor-default',
          dropzoneState === 'error' && 'border-destructive/50 bg-destructive/5 cursor-default',
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept="application/pdf,.pdf,image/*,.doc,.docx,.pages,.rtf,.txt"
          multiple
          onChange={handleFileSelect}
        />
        <input
          ref={addMoreInputRef}
          type="file"
          className="hidden"
          accept="application/pdf,.pdf,image/*,.doc,.docx,.pages,.rtf,.txt"
          multiple
          onChange={handleFileSelect}
        />
        <input
          ref={cameraInputRef}
          type="file"
          className="hidden"
          accept="image/*"
          capture="environment"
          onChange={handleFileSelect}
        />
        <canvas ref={canvasRef} className="hidden" />

        {/* WEBCAM VIEWFINDER (desktop) */}
        {cameraActive && (
          <div className="flex flex-col items-center gap-4 py-6 px-6" onClick={e => e.stopPropagation()}>
            <div className="relative w-full max-w-md rounded-lg overflow-hidden bg-black">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full rounded-lg"
              />
              <div className="absolute top-2 right-2">
                <Badge variant="secondary" className="text-[10px] bg-black/60 text-white border-0">
                  LIVE
                </Badge>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                className="gap-2 border-white/30 text-white hover:bg-white/10 hover:text-white"
                onClick={stopWebcam}
              >
                <X className="w-4 h-4" />
                Cancel
              </Button>
              <Button
                size="sm"
                className="gap-2 bg-primary hover:bg-primary/90 px-6"
                onClick={captureFrame}
              >
                <Camera className="w-4 h-4" />
                Capture
              </Button>
            </div>
            <p className="text-[10px] text-white/60">Position clinical document in frame, then click Capture</p>
          </div>
        )}

        {/* IDLE */}
        {(dropzoneState === 'idle' || dropzoneState === 'dragging') && !cameraActive && (
          <div className="flex flex-col items-center justify-center gap-4 py-12 px-6 text-center">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg bg-sky-50 border border-sky-200 flex items-center justify-center">
                <Camera className="w-5 h-5 text-sky-600" />
              </div>
              <div className="w-11 h-11 rounded-lg bg-sky-50 border border-sky-200 flex items-center justify-center">
                <FileImage className="w-5 h-5 text-sky-600" />
              </div>
              <div className="w-11 h-11 rounded-lg bg-sky-50 border border-sky-200 flex items-center justify-center">
                <Images className="w-5 h-5 text-sky-600" />
              </div>
            </div>
            <div className="space-y-1.5">
              <p className="text-sm font-medium text-foreground">
                {dropzoneState === 'dragging'
                  ? 'Drop files here…'
                  : 'Upload multi-page documents — take photos, drop files, or paste screenshots'}
              </p>
              <p className="text-xs text-muted-foreground">
                Stage multiple pages, then process them all at once as a single clinical document.
              </p>
            </div>
            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row items-center gap-3 mt-1 w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                className="gap-2 w-full sm:w-auto border-sky-300 text-sky-700 hover:bg-sky-50"
                onClick={handleTakePhoto}
              >
                <Camera className="w-4 h-4" />
                Take Photo
              </Button>
              <span className="text-xs text-muted-foreground hidden sm:inline">or</span>
              <Button
                variant="ghost"
                size="sm"
                className="gap-2 text-muted-foreground w-full sm:w-auto"
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
              >
                <Upload className="w-4 h-4" />
                Browse Files
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground">Max 20 MB per file · Multiple files supported</p>
          </div>
        )}

        {/* STAGING: Thumbnail strip + Process button */}
        {dropzoneState === 'staging' && !cameraActive && (
          <div className="py-5 px-5 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Images className="w-4 h-4 text-sky-600" />
                <p className="text-sm font-medium text-foreground">
                  {stagedFiles.length} page{stagedFiles.length > 1 ? 's' : ''} staged
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1.5 text-muted-foreground h-8"
                  onClick={handleReset}
                >
                  <X className="w-3.5 h-3.5" /> Clear All
                </Button>
              </div>
            </div>

            {/* Thumbnail strip */}
            <ScrollArea className="w-full">
              <div className="flex gap-3 pb-2">
                {stagedFiles.map((sf, idx) => (
                  <div
                    key={sf.id}
                    className="relative group shrink-0 w-20 h-20 rounded-lg border border-border bg-muted/30 overflow-hidden"
                  >
                    {sf.thumbnailUrl ? (
                      <img
                        src={sf.thumbnailUrl}
                        alt={`Page ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-1">
                        <FileText className="w-5 h-5 text-muted-foreground" />
                        <span className="text-[9px] text-muted-foreground truncate max-w-[70px] px-1">
                          {sf.name.split('.').pop()?.toUpperCase()}
                        </span>
                      </div>
                    )}
                    {/* Page number badge */}
                    <div className="absolute bottom-1 left-1">
                      <Badge variant="secondary" className="text-[9px] h-4 px-1 bg-black/60 text-white border-0">
                        {idx + 1}
                      </Badge>
                    </div>
                    {/* Remove button */}
                    <button
                      onClick={() => removeStagedFile(sf.id)}
                      className="absolute top-1 right-1 w-5 h-5 rounded-full bg-destructive/90 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}

                {/* Add more button */}
                <button
                  onClick={() => addMoreInputRef.current?.click()}
                  className="shrink-0 w-20 h-20 rounded-lg border-2 border-dashed border-sky-300 hover:border-sky-500 bg-sky-50/50 hover:bg-sky-50 flex flex-col items-center justify-center gap-1 transition-colors"
                >
                  <Plus className="w-5 h-5 text-sky-500" />
                  <span className="text-[10px] text-sky-600 font-medium">Add Page</span>
                </button>
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>

            {/* Process button */}
            <Button
              onClick={processBatch}
              className="w-full gap-2"
              size="default"
            >
              <Sparkles className="w-4 h-4" />
              Process {stagedFiles.length} Page{stagedFiles.length > 1 ? 's' : ''} as Single Document
            </Button>

            <p className="text-[10px] text-muted-foreground text-center">
              All pages will be sent to the AI as one contiguous document for unified extraction.
            </p>
          </div>
        )}

        {/* PROCESSING */}
        {dropzoneState === 'processing' && (
          <div className="flex flex-col items-center justify-center gap-4 py-14 px-6 text-center">
            <div className="relative">
              <div className="w-14 h-14 rounded-full bg-sky-100 flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-sky-600 animate-spin" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-sky-600 flex items-center justify-center">
                <Sparkles className="w-3 h-3 text-white" />
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">
                AI is extracting from {stagedFiles.length} page{stagedFiles.length > 1 ? 's' : ''}…
              </p>
              <p className="text-xs text-muted-foreground">
                {stagedFiles.map(f => f.name).join(', ')}
              </p>
            </div>
            <div className="w-56 h-2 rounded-full bg-sky-100 overflow-hidden">
              <div className="h-full bg-sky-500 rounded-full animate-pulse" style={{ width: '70%' }} />
            </div>
          </div>
        )}

        {/* ERROR */}
        {dropzoneState === 'error' && (
          <div className="flex flex-col items-center justify-center gap-4 py-10 px-6 text-center">
            <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-destructive" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">Extraction Failed</p>
              <p className="text-xs text-muted-foreground">{errorMessage}</p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button variant="outline" size="sm" onClick={handleReset}>
                Try Again
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setIntakeMode('manual');
                  setDropzoneState('idle');
                  setErrorMessage('');
                }}
              >
                Use Manual Entry
              </Button>
            </div>
          </div>
        )}

        {/* COMPLETE (compact) */}
        {dropzoneState === 'complete' && (
          <div className="flex items-center justify-between gap-3 py-4 px-5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">
                  {stagedFiles.length} page{stagedFiles.length > 1 ? 's' : ''} processed
                </p>
                <p className="text-xs text-muted-foreground">
                  {stagedFiles.map(f => f.name).join(', ')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Reset button — bottom left, outside dropzone */}
      {intakeMode === 'upload' && dropzoneState === 'complete' && (
        <div className="flex justify-start">
          <Button variant="ghost" size="sm" className="text-muted-foreground gap-1.5" onClick={handleReset}>
            <X className="w-4 h-4" /> Reset
          </Button>
        </div>
      )}

      {/* Extracted Patient Card */}
      {intakeMode === 'upload' && dropzoneState === 'complete' && extractedData && (
        <Card className="border-primary/20 bg-card shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-300">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-semibold text-foreground">Extracted Patient Data</h3>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className={cn("text-[10px]", extractionConfidenceColor)}>
                  {extractedData.extraction_confidence?.toUpperCase()} CONFIDENCE
                </Badge>
                <Badge variant="secondary" className="text-[10px]">AI Extracted</Badge>
              </div>
            </div>

            {extractedData.extraction_notes && (
              <div className="flex items-start gap-2 p-2 rounded-md bg-muted/50 text-xs text-muted-foreground">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span>{extractedData.extraction_notes}</span>
              </div>
            )}

            {/* Demographics grid — editable fallbacks for missing fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                <User className="w-4 h-4 text-primary shrink-0" />
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Patient</p>
                  {extractedData.patient_name ? (
                    <p className="text-sm font-medium text-foreground">{extractedData.patient_name}</p>
                  ) : (
                    <Input
                      value={editableName}
                      onChange={e => setEditableName(e.target.value)}
                      placeholder="Enter Name"
                      className="h-7 text-sm mt-0.5"
                    />
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                <Calendar className="w-4 h-4 text-primary shrink-0" />
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">DOB</p>
                  {extractedData.patient_dob ? (
                    <p className="text-sm font-medium text-foreground">{extractedData.patient_dob}</p>
                  ) : (
                    <Input
                      value={editableDob}
                      onChange={e => setEditableDob(e.target.value)}
                      placeholder="Enter DOB"
                      className="h-7 text-sm mt-0.5"
                    />
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                <Hash className="w-4 h-4 text-primary shrink-0" />
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">MRN</p>
                  {extractedData.patient_mrn ? (
                    <p className="text-sm font-medium text-foreground">{extractedData.patient_mrn}</p>
                  ) : (
                    <Input
                      value={editableMrn}
                      onChange={e => setEditableMrn(e.target.value)}
                      placeholder="Enter MRN"
                      className="h-7 text-sm mt-0.5"
                    />
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                <Stethoscope className="w-4 h-4 text-primary shrink-0" />
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Clinical Context</p>
                  <p className="text-sm font-medium text-foreground">{clinicalContext || 'Pending extraction…'}</p>
                </div>
              </div>
            </div>

            {/* Detected Procedure + Matched Template */}
            {detectedProcedureText && (
              <div className="space-y-2 p-3 rounded-lg bg-primary/5 border border-primary/20">
                <div className="flex items-start gap-3">
                  <FileCheck className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Detected Procedure</p>
                    <p className="text-sm font-medium text-foreground italic">"{detectedProcedureText}"</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Sparkles className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Matched Template</p>
                    <div className="flex items-center gap-2 flex-wrap mt-0.5">
                      {matchedTemplate ? (
                        <>
                          <p className="text-sm font-semibold text-foreground">{matchedTemplate.template_name}</p>
                          <Badge variant="outline" className={cn('text-[10px] border', confidenceColor)}>
                            {confidenceLabel} Match
                          </Badge>
                        </>
                      ) : (
                        <p className="text-sm text-muted-foreground">No matching template found</p>
                      )}
                      <Popover open={templatePopoverOpen} onOpenChange={setTemplatePopoverOpen}>
                        <PopoverTrigger asChild>
                          <button
                            className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-medium transition-colors cursor-pointer ml-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Pencil className="w-3 h-3" />
                            Change
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80 p-0" align="start">
                          <div className="p-3 border-b">
                            <div className="relative">
                              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                              <Input
                                placeholder="Search templates…"
                                value={templateSearchQuery}
                                onChange={e => setTemplateSearchQuery(e.target.value)}
                                className="pl-8 h-8 text-sm"
                                autoFocus
                              />
                            </div>
                          </div>
                          <div className="max-h-52 overflow-y-auto p-1">
                            {filteredTemplates.length === 0 ? (
                              <p className="text-xs text-muted-foreground text-center py-4">No templates found</p>
                            ) : (
                              filteredTemplates.map(t => (
                                <button
                                  key={t.id}
                                  className={cn(
                                    'w-full text-left px-3 py-2 rounded-md text-sm transition-colors hover:bg-muted',
                                    matchedTemplate?.id === t.id && 'bg-primary/10 text-primary font-medium'
                                  )}
                                  onClick={() => handleTemplateOverride(t)}
                                >
                                  <p className="font-medium text-foreground text-sm">{t.template_name}</p>
                                  <p className="text-[10px] text-muted-foreground">
                                    {t.specialty} · {t.body_region || 'General'} · {t.primary_cpts.length} CPTs
                                  </p>
                                </button>
                              ))
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <Button
              onClick={handleProceed}
              className="w-full"
              disabled={!(extractedData.patient_name || editableName.trim())}
            >
              {matchedTemplate ? 'Proceed with Matched Template' : 'Proceed to Template Selection'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}

    </div>
  );
}
